import React, { useState, useEffect, useMemo, useRef, useContext, createContext } from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Card,
    Typography,
    Grid,
    Avatar,
    Button,
    styled,
    useTheme,
    useMediaQuery,
    Fab,
    Zoom,
    Tooltip,
    Stack,
    IconButton
} from "@mui/material";

/* ✅ ProDev Assets & Icons */
import { AiFillBulb, AiOutlineRead } from "react-icons/ai";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CodeIcon from "@mui/icons-material/Code";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import StopIcon from "@mui/icons-material/Stop";

const bgImage = "https://images.unsplash.com/photo-1633356122544-f134324a6cee";

/* ✅ Context Architecture */
const ProDevThemeContext = createContext("Dark-Gold");

/* ✅ Styled Components - ProDev Dark Glass Theme */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.9), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(16,7,32,0.85)",
    borderRadius: "14px",
    padding: theme.spacing(3),
    marginBottom: "25px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
    border: "1px solid rgba(255,255,255,0.1)",
    color: "white",
}));

const CodeBlock = styled("pre")(({ theme }) => ({
    backgroundColor: "#0d1117",
    color: "#c9d1d9",
    padding: "20px",
    borderRadius: "10px",
    overflowX: "auto",
    fontSize: "0.9rem",
    lineHeight: 1.6,
    border: "1px solid rgba(255,255,255,0.05)",
    fontFamily: "'Fira Code', 'Courier New', monospace",
}));

const ActionButton = styled(Button)(({ theme }) => ({
    backgroundColor: "goldenrod",
    color: "#100720",
    fontWeight: 800,
    padding: "12px 40px",
    borderRadius: "999px",
    transition: "0.3s",
    "&:hover": { backgroundColor: "#e0b200", transform: "scale(1.05)" },
}));

const LiveDemoBox = styled(Box)(({ theme }) => ({
    padding: "20px",
    borderRadius: "12px",
    backgroundColor: "rgba(255, 255, 255, 0.05)",
    border: "1px dashed goldenrod",
    textAlign: "center",
    marginTop: "15px"
}));

const ProDevInput = styled("input")({
    padding: "10px",
    borderRadius: "8px",
    border: "1px solid goldenrod",
    backgroundColor: "transparent",
    color: "white",
    marginBottom: "10px",
    width: "80%",
    outline: "none"
});

/**
 * HooksDemo Component
 * Specialized ProDevConsult Module
 * Author: Hazzan
 */
export default function HooksDemo() {
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down("sm"));

    /* ✅ Hooks Initialization */
    const [count, setCount] = useState(1);
    const [message, setMessage] = useState("Initializing Core...");
    const inputRef = useRef(null);
    const proDevTheme = useContext(ProDevThemeContext);

    const [showScroll, setShowScroll] = useState(false);
    const [copiedIndex, setCopiedIndex] = useState(null);
    const [isSpeaking, setIsSpeaking] = useState(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setMessage("ProDev Hooks: System Active! 🚀");
        }, 2000);

        const checkScroll = () => setShowScroll(window.pageYOffset > 400);
        window.addEventListener("scroll", checkScroll);

        return () => {
            clearTimeout(timer);
            window.removeEventListener("scroll", checkScroll);
        };
    }, []);

    const handleSpeak = (text) => {
        window.speechSynthesis.cancel();
        if (isSpeaking) { setIsSpeaking(false); return; }
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.onstart = () => setIsSpeaking(true);
        utterance.onend = () => setIsSpeaking(false);
        window.speechSynthesis.speak(utterance);
    };

    const handleCopy = (text, index) => {
        navigator.clipboard.writeText(text);
        setCopiedIndex(index);
        setTimeout(() => setCopiedIndex(null), 2000);
    };

    const increaseCount = () => {
        if (count < 4) setCount(prev => prev + 1);
    };

    const hooksModules = useMemo(() => [
        {
            title: "1. useState: Reactive State",
            tag: "Local Memory",
            desc: "useState is the baseline for component memory. It allows functional units to track data changes and trigger UI updates automatically.",
            code: `const [count, setCount] = useState(1);\n\nconst add = () => setCount(count + 1);`
        },
        {
            title: "2. useEffect: Lifecycle Manager",
            tag: "Side Effects",
            desc: "Use this to perform tasks after rendering, such as API calls or starting timers. It replaces old lifecycle methods like componentDidMount.",
            code: `useEffect(() => {\n  const timer = setTimeout(() => setMessage("Active"), 2000);\n  return () => clearTimeout(timer); // Cleanup logic\n}, []);`
        },
        {
            title: "3. useRef: Direct DOM Access",
            tag: "Mutable References",
            desc: "useRef creates a persistent object that doesn't trigger re-renders. We use it at ProDevConsult for focusing inputs or storing mutable values.",
            code: `const inputRef = useRef(null);\nconst focusNode = () => inputRef.current.focus();`
        },
        {
            title: "4. useContext: Global State",
            tag: "Prop-Drilling Fix",
            desc: "Avoid passing data through every level of your tree. useContext allows components to tap directly into a global data stream.",
            code: `const ThemeContext = createContext("Dark");\nconst theme = useContext(ThemeContext);`
        }
    ], []);

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6, px: 2 }}>
                <Typography variant={isMobile ? "h4" : "h3"} sx={{
                    fontWeight: 800, backgroundColor: "gold", color: "#100720",
                    px: { xs: 3, md: 6 }, py: 1.5, borderRadius: "999px",
                    display: "inline-block", boxShadow: "0 6px 20px rgba(218, 165, 32, 0.3)",
                }}>
                    REACT HOOKS PATH
                </Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                            <Typography variant="h5" fontWeight={800} color="goldenrod">
                                Overview: Functional Purity
                            </Typography>
                            <IconButton onClick={() => handleSpeak("React Hooks allow functional components to have state and lifecycle methods. In this ProDevConsult module, Hazzan covers the four essential hooks.")} sx={{ color: "goldenrod" }}>
                                <VolumeUpIcon />
                            </IconButton>
                        </Stack>
                        <Typography variant="body1" sx={{ opacity: 0.9, lineHeight: 1.7 }}>
                            Hooks solve the problem of code reusability and complex class logic. By using <b>useState</b>, <b>useEffect</b>, <b>useRef</b>, and <b>useContext</b>, we build lean, modular applications that are easy to test and maintain.
                        </Typography>
                    </SectionCard>
                </Grid>

                {hooksModules.map((item, i) => (
                    <Grid item xs={12} key={i}>
                        <SectionCard>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                    <Avatar sx={{ bgcolor: "rgba(218, 165, 32, 0.2)", color: "goldenrod" }}><AiOutlineRead /></Avatar>
                                    <Box>
                                        <Typography variant="h6" fontWeight={700}>{item.title}</Typography>
                                        <Typography variant="caption" color="goldenrod" fontWeight={800}>{item.tag}</Typography>
                                    </Box>
                                </Box>
                                <Tooltip title={copiedIndex === i ? "Copied!" : "Copy Code"}>
                                    <IconButton onClick={() => handleCopy(item.code, i)} sx={{ color: "white" }}>
                                        {copiedIndex === i ? <CheckIcon /> : <ContentCopyIcon />}
                                    </IconButton>
                                </Tooltip>
                            </Box>

                            <Typography variant="body1" sx={{ mb: 2, opacity: 0.9 }}>{item.desc}</Typography>
                            <CodeBlock>{item.code}</CodeBlock>

                            {/* Live Demo Interactions */}
                            <LiveDemoBox>
                                {i === 0 && (
                                    <Box>
                                        <Typography variant="h6" sx={{ mb: 1 }}>Count: {count}</Typography>
                                        <Button variant="contained" sx={{ bgcolor: "goldenrod", color: "black" }} onClick={increaseCount} disabled={count >= 4}>Increase State</Button>
                                    </Box>
                                )}
                                {i === 1 && <Typography variant="h6">{message}</Typography>}
                                {i === 2 && (
                                    <Box>
                                        <ProDevInput ref={inputRef} placeholder="Hazzan's secure input..." />
                                        <br/>
                                        <Button variant="contained" sx={{ bgcolor: "goldenrod", color: "black" }} onClick={() => inputRef.current.focus()}>Focus Node</Button>
                                    </Box>
                                )}
                                {i === 3 && <Typography variant="h6">Current Global Theme: <b>{proDevTheme}</b></Typography>}
                            </LiveDemoBox>
                        </SectionCard>
                    </Grid>
                ))}

                <Grid item xs={12} textAlign="center" sx={{ mt: 4, mb: 10 }}>
                    <Link to="/javascriptcoding" style={{ textDecoration: "none" }}>
                        <ActionButton variant="contained">FINISH ARCHITECTURE SERIES</ActionButton>
                    </Link>
                </Grid>
            </Grid>

            {/* ✅ Floating Utilities */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                {isSpeaking && (
                    <Zoom in={isSpeaking}>
                        <Fab size="medium" color="error" onClick={() => window.speechSynthesis.cancel()}><StopIcon /></Fab>
                    </Zoom>
                )}
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: "goldenrod" }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </PageWrapper>
    );
}
